/**
 * Корень приложения: стартовая последовательность, блокирующие состояния
 * и глобальные индикаторы.
 *
 * Блокирующие экраны SYS-03/05/07/08/09 рисуются ПОВЕРХ маршрутов, а не
 * вместо них: после снятия блокировки пользователь остаётся там же, где был.
 */
import { RouterProvider } from '@tanstack/react-router'
import { useEffect } from 'react'

import { reducedMotion } from '@shared/lib/motion'
import { MEDIA } from '@shared/lib/use-media-query'
import { browserOwnsBackGesture } from '@shared/pwa/platform'
import { applyUpdate } from '@shared/pwa/register'
import { GlobalBar } from '@shared/ui'

import { bootstrap } from './boot/bootstrap'
import { useAppStore } from './model/app-store'
import { getCurrentKey, setSkipTraversalEnter, trackNavigation } from './model/navigation-direction'
import { captureScreenScroll } from './model/screen-scroll'
import { NAV_NESTED_ATTR, NAV_SCREEN_ATTR, captureScreenSnapshot } from './model/screen-snapshots'
import { startThemeColorSync } from './model/theme-color'
import { startViewportSync } from './model/viewport'
import { Providers } from './providers/Providers'
import { router } from './routes/router'
import { BootScreen } from './screens/system/BootScreen'

export function App(): React.ReactElement {
  return (
    <Providers>
      <AppBody />
    </Providers>
  )
}

/** Корневые разделы панели вкладок: переход между ними не анимируется (ScreenFrame). */
const ROOT_SECTIONS = new Set(['/dvizh', '/contacts', '/chats', '/settings'])

function isRootSection(pathname: string): boolean {
  return ROOT_SECTIONS.has(pathname)
}

/** Текущий экран (DOM ещё прежний в момент смены записи) — вложенный, с «назад». */
function currentScreenNested(): boolean {
  return document.querySelector(`[${NAV_SCREEN_ATTR}]`)?.hasAttribute(NAV_NESTED_ATTR) ?? false
}

function AppBody(): React.ReactElement {
  const phase = useAppStore((s) => s.phase)
  const bootError = useAppStore((s) => s.bootError)

  useEffect(() => {
    void bootstrap()
  }, [])

  /*
   * Направление переходов для анимации экранов и снимок уходящего экрана:
   * подписка срабатывает при смене записи истории, когда DOM ещё прежний, —
   * под входящим экраном будет лежать именно тот, с которого ушли (ScreenFrame).
   */
  useEffect(() => {
    // Safari на iPhone во вкладке анимирует возврат по истории сам — приложение его не повторяет.
    setSkipTraversalEnter(browserOwnsBackGesture())
    trackNavigation(router.history.location.state.key, router.history.location.pathname)
    return router.history.subscribe(({ location }) => {
      /*
       * Снимки нужны только там, где экраны двигаются: одна колонка и без reduce.
       * Переключение разделов не анимируется — копировать DOM списка ради него незачем
       * (на телефоне это заметная часть стоимости перехода).
       */
      if (
        !globalThis.matchMedia?.(MEDIA.fromDesktop).matches &&
        !reducedMotion() &&
        !(isRootSection(location.pathname) && !currentScreenNested())
      ) {
        captureScreenSnapshot(getCurrentKey())
      }
      // Позиция прокрутки нужна и там, где движения нет: возврат на раздел не должен прыгать наверх
      captureScreenScroll(getCurrentKey())
      trackNavigation(location.state.key, location.pathname)
    })
  }, [])

  // Цвет системной области следует за шапкой текущего экрана (theme-color.ts).
  useEffect(() => startThemeColorSync(router.history), [])

  // Экранная клавиатура на iOS: раскладка сжимается до видимой области, шапки остаются (viewport.ts).
  useEffect(() => startViewportSync(), [])

  if (phase === 'boot') return <BootScreen />
  if (phase === 'failed') return <BootScreen error={bootError} />

  return (
    <>
      <StatusBars />
      <RouterProvider router={router} />
    </>
  )
}

/**
 * SYS-01 «Нет сети», SYS-11 «Синхронизация», SYS-04 «Доступно обновление».
 * ПС §19: ненавязчивый индикатор, приложение не перекрывается модальным окном.
 */
function StatusBars(): React.ReactElement | null {
  const online = useAppStore((s) => s.online)
  const syncStatus = useAppStore((s) => s.syncStatus)
  const updateAvailable = useAppStore((s) => s.updateAvailable)

  if (!online) {
    // PWA-04: в офлайне экраны показывают реплику, и это проговаривается явно.
    return (
      <GlobalBar tone="offline">
        Нет соединения — данные могут быть неактуальны, изменения уйдут позже
      </GlobalBar>
    )
  }

  if (updateAvailable) {
    return (
      /* §9.5 п. 3: SKIP_WAITING → controllerchange → reload, а не голый reload. */
      <GlobalBar tone="update" action={{ label: 'Перезапустить', onClick: () => void applyUpdate() }}>
        Доступно обновление
      </GlobalBar>
    )
  }

  /*
   * SYS-12 «Полная пересинхронизация»: cursor вышел за retention
   * (410 `sync_reset_required`), и локальная копия перезагружается целиком.
   * Это дольше обычного догона, поэтому состояние называется своим именем.
   */
  if (syncStatus === 'reset_required') {
    return <GlobalBar tone="syncing">Обновляем данные заново — это займёт немного больше времени</GlobalBar>
  }

  // Состояние не считается актуальным, пока не завершён replay (CC §4.1 п. 6).
  if (syncStatus === 'initial_snapshot' || syncStatus === 'replaying') {
    return <GlobalBar tone="syncing">Синхронизация…</GlobalBar>
  }

  return null
}
